import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { GitCompareArrows, ArrowRight, Share2, Check } from "lucide-react";
import { getStates, getStateSymbols, getCompareFacts } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { Seo } from "@/components/site/Seo";
import { SectionHeading } from "@/components/home/ExploreByState";
import { toast } from "sonner";

const ROWS = [
  ["nickname", "Nickname"], ["motto", "Motto"], ["bird", "Bird"], ["flower", "Flower"],
  ["tree", "Tree"], ["mammal", "Mammal / Animal"], ["fish", "Fish"], ["insect", "Insect"], ["song", "Song"],
];

function StatePicker({ label, value, onChange, options, testid }) {
  return (
    <label className="flex flex-1 flex-col gap-1">
      <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground"><T>{label}</T></span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-border bg-white px-3 py-2.5 text-sm font-semibold text-navy outline-none focus:border-navy"
        data-testid={testid}
      >
        {options.map((s) => <option key={s.geo_id} value={s.geo_id}>{s.official_name}</option>)}
      </select>
    </label>
  );
}

function Panel({ data }) {
  if (!data) return <div className="animate-pulse rounded-xl border border-border bg-white p-6 text-center text-navy">Loading…</div>;
  if (!data.available) return <div className="rounded-xl border border-border bg-white p-6 text-center text-muted-foreground">No verified symbols for this place.</div>;
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={`compare-panel-${data.geo_id}`}>
      <div className="mb-3 flex items-center gap-3">
        {data.flag?.img && <img src={data.flag.img} alt={`Flag of ${data.name}`} className="h-8 w-auto rounded border border-border" />}
        <Link to={`/place/us/${data.geo_id}`} className="font-display text-lg font-black text-navy hover:text-oldglory-red">{data.name}</Link>
      </div>
    </div>
  );
}

export default function SymbolCompare() {
  const [states, setStates] = useState([]);
  const [params, setParams] = useSearchParams();
  const a = params.get("a") || "oregon";
  const b = params.get("b") || "california";
  const [da, setDa] = useState(null);
  const [db, setDb] = useState(null);
  const [fa, setFa] = useState(null);
  const [fb, setFb] = useState(null);

  useEffect(() => { getStates().then((d) => setStates(d.states.filter((s) => s.geo_type !== "territory" || true))).catch(() => {}); }, []);
  useEffect(() => { setDa(null); getStateSymbols(a).then(setDa).catch(() => setDa({ available: false })); }, [a]);
  useEffect(() => { setDb(null); getStateSymbols(b).then(setDb).catch(() => setDb({ available: false })); }, [b]);
  useEffect(() => { setFa(null); getCompareFacts(a).then(setFa).catch(() => {}); }, [a]);
  useEffect(() => { setFb(null); getCompareFacts(b).then(setFb).catch(() => {}); }, [b]);

  const setA = (v) => setParams({ a: v, b });
  const setB = (v) => setParams({ a, b: v });

  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = `${window.location.origin}/symbols/compare?a=${a}&b=${b}`;
    const title = `Compare ${da?.name || a} vs ${db?.name || b} — USAPulse`;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success("Link copied to clipboard");
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (_) { /* user cancelled share */ }
  };

  const rowMap = (d) => {
    const m = {};
    (d?.symbols || []).forEach((s) => { m[s.key] = s; });
    return m;
  };
  const ma = useMemo(() => rowMap(da), [da]);
  const mb = useMemo(() => rowMap(db), [db]);
  const activeRows = ROWS.filter(([k]) => ma[k] || mb[k]);

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-12">
      <Seo
        title="Compare State Symbols"
        path="/symbols/compare"
        description="Compare two U.S. states side by side — their official bird, flower, tree, motto, animal and state song, each verified from state statutes."
      />
      <SectionHeading kicker="Symbol Compare" title="Compare two states, symbol by symbol" />

      <div className="mb-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-end" data-testid="compare-controls">
        <StatePicker label="State A" value={a} onChange={setA} options={states} testid="compare-select-a" />
        <span className="hidden items-center justify-center pb-2 text-oldglory-red sm:flex"><GitCompareArrows className="h-5 w-5" /></span>
        <StatePicker label="State B" value={b} onChange={setB} options={states} testid="compare-select-b" />
        <button
          onClick={share}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-navy/20 bg-white px-4 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-oldglory-red hover:text-oldglory-red sm:pb-2.5"
          data-testid="compare-share-btn"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Share2 className="h-4 w-4" />}
          <T>{copied ? "Copied!" : "Share"}</T>
        </button>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-4">
        <Panel data={da} />
        <Panel data={db} />
      </div>

      {(fa?.facts?.length > 0 || fb?.facts?.length > 0) && (() => {
        const rmA = Object.fromEntries((fa?.facts || []).map((f) => [f.key, f]));
        const rmB = Object.fromEntries((fb?.facts || []).map((f) => [f.key, f]));
        const order = ["population", "area", "density", "gdp", "income", "capital", "largest_city", "admitted", "region", "climate"];
        const keys = order.filter((k) => rmA[k] || rmB[k]);
        return (
          <div className="mb-8 overflow-hidden rounded-xl border border-border" data-testid="compare-facts-table">
            <div className="bg-navy px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-white"><T>Key Facts &amp; Climate</T></div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-border">
                {keys.map((k) => {
                  const va = rmA[k], vb = rmB[k];
                  const label = (va || vb).label;
                  const live = (va || vb).live;
                  return (
                    <tr key={k} data-testid={`compare-fact-${k}`}>
                      <td className="w-1/3 px-3 py-2 font-semibold text-navy"><T>{label}</T>{live && <span className="ml-1.5 rounded bg-oldglory-red/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-oldglory-red">live</span>}</td>
                      <td className="px-3 py-2 text-ink">{va ? va.value : <span className="text-muted-foreground">—</span>}{va?.note && <span className="block text-[11px] text-muted-foreground">{va.note}</span>}</td>
                      <td className="px-3 py-2 text-ink">{vb ? vb.value : <span className="text-muted-foreground">—</span>}{vb?.note && <span className="block text-[11px] text-muted-foreground">{vb.note}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      <div className="overflow-hidden rounded-xl border border-border" data-testid="compare-table">
        <table className="w-full text-sm">
          <thead className="bg-parchment/60 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-bold"><T>Symbol</T></th>
              <th className="px-3 py-2 text-left font-bold">{da?.name || "State A"}</th>
              <th className="px-3 py-2 text-left font-bold">{db?.name || "State B"}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {activeRows.map(([key, label]) => {
              const va = ma[key], vb = mb[key];
              const same = va && vb && va.value === vb.value;
              return (
                <tr key={key} data-testid={`compare-row-${key}`} className={same ? "bg-emerald-50/50" : ""}>
                  <td className="px-3 py-2 font-semibold text-navy"><T>{label}</T></td>
                  <td className="px-3 py-2 text-ink">{va ? va.value : <span className="text-muted-foreground">—</span>}{va?.note && <span className="block text-[11px] italic text-muted-foreground">{va.note}</span>}</td>
                  <td className="px-3 py-2 text-ink">{vb ? vb.value : <span className="text-muted-foreground">—</span>}{vb?.note && <span className="block text-[11px] italic text-muted-foreground">{vb.note}</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        <T>Rows highlighted in green are shared by both states. Every symbol is verified from official state statutes.</T>
      </p>
      <Link to="/explore/states" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-oldglory-red hover:underline">
        <T>Explore all states</T> <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
